class FlavorGallery {
  constructor(galleryElement) {
    this.gallery = galleryElement;
    this.galleryId = this.gallery.id;
    this.mediaItems = this.gallery.querySelectorAll('.flavor-tasting__media-item');
    this.currentItemIndex = 0;
    
    // Find navigation buttons for this specific gallery
    const flavorContainer = this.gallery.closest('.flavor-tasting__flavor-item');
    if (flavorContainer) {
      this.prevButton = flavorContainer.querySelector('.flavor-tasting__prev-button');
      this.nextButton = flavorContainer.querySelector('.flavor-tasting__next-button');
      this.contentItems = flavorContainer.querySelectorAll('.flavor-tasting__flavor-content');
    }
    
    if (!this.mediaItems.length) return;
    
    this.totalItems = this.mediaItems.length;
    
    // Initialize first item as active if none are
    if (!this.gallery.querySelector('.flavor-tasting__media-item.active')) {
      this.mediaItems[0].classList.add('active');
      if (this.contentItems.length > 0) {
        this.contentItems[0].classList.add('active');
      }
    } else {
      // Find current active index
      this.mediaItems.forEach((item, index) => {
        if (item.classList.contains('active')) {
          this.currentItemIndex = index;
        }
      });
      // Make sure corresponding content is active
      if (this.contentItems.length > this.currentItemIndex) {
        this.contentItems.forEach(content => content.classList.remove('active'));
        this.contentItems[this.currentItemIndex].classList.add('active');
      }
    }
    
    // Set initial border color based on active flavor
    this.updateBorderColor();
    
    // Show navigation if we have multiple items (flavors)
    if (this.totalItems > 1) {
      this.setupEventListeners();
      this.showNavigation();
    } else {
      this.hideNavigation();
    }
  }
  
  setupEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.showPrevItem();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.showNextItem();
      });
    }
    
    // Add touch/swipe support
    this.gallery.addEventListener('touchstart', this.handleTouchStart.bind(this), {passive: true});
    this.gallery.addEventListener('touchmove', this.handleTouchMove.bind(this), {passive: false});
    this.gallery.addEventListener('touchend', this.handleTouchEnd.bind(this), {passive: true});
  }
  
  showNavigation() {
    if (this.prevButton) this.prevButton.style.display = 'flex';
    if (this.nextButton) this.nextButton.style.display = 'flex';
  }
  
  hideNavigation() {
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';
  }
  
  showPrevItem() {
    this.currentItemIndex = (this.currentItemIndex - 1 + this.totalItems) % this.totalItems;
    this.updateGallery();
  }
  
  showNextItem() {
    this.currentItemIndex = (this.currentItemIndex + 1) % this.totalItems;
    this.updateGallery();
  }
  
  updateGallery() {
    // Remove active class from all media items
    this.mediaItems.forEach(item => {
      item.classList.remove('active');
    });
    
    // Add active class to current media item
    this.mediaItems[this.currentItemIndex].classList.add('active');
    
    // Update border color based on current flavor
    this.updateBorderColor();
    
    // Update corresponding content if it exists
    if (this.contentItems && this.contentItems.length > this.currentItemIndex) {
      this.contentItems.forEach(content => {
        content.classList.remove('active');
      });
      this.contentItems[this.currentItemIndex].classList.add('active');
    }
  }
  
  updateBorderColor() {
    const currentFlavorColor = this.mediaItems[this.currentItemIndex].dataset.flavorColor;
    const flavorContainer = this.gallery.closest('.flavor-tasting__flavor-item');
    
    if (flavorContainer && currentFlavorColor) {
      flavorContainer.style.setProperty('--flavor-border', currentFlavorColor);
    }
  }
  
  // Touch handling for swipes
  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = new Date().getTime();
  }
  
  handleTouchMove(event) {
    if (!this.touchStartX || !this.touchStartY) return;
    
    const xDiff = this.touchStartX - event.touches[0].clientX;
    const yDiff = this.touchStartY - event.touches[0].clientY;
    
    // Prevent vertical scrolling if horizontal swipe is detected
    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      event.preventDefault();
    }
  }
  
  handleTouchEnd(event) {
    if (!this.touchStartX || !this.touchStartY) return;
    
    const xDiff = this.touchStartX - event.changedTouches[0].clientX;
    const yDiff = this.touchStartY - event.changedTouches[0].clientY;
    const elapsedTime = new Date().getTime() - this.touchStartTime;
    
    // Only register as swipe if:
    // 1. Horizontal movement is greater than vertical
    // 2. Movement is significant enough (>50px)
    // 3. Swipe was fast enough (<300ms)
    if (Math.abs(xDiff) > Math.abs(yDiff) && Math.abs(xDiff) > 50 && elapsedTime < 300) {
      if (xDiff > 0) {
        // Swipe left, show next
        this.showNextItem();
      } else {
        // Swipe right, show previous
        this.showPrevItem();
      }
    }
    
    // Reset touch values
    this.touchStartX = null;
    this.touchStartY = null;
  }
}

// Fix for meter display
function fixMeters() {
  const meters = document.querySelectorAll('.flavor-tasting__meter');
  
  meters.forEach(meter => {
    // Get the current style attribute
    const style = meter.getAttribute('style');
    
    if (style && style.includes('width:')) {
      // It already has a width, make sure it's applied correctly
      const width = style.match(/width:\s*(\d+)%/);
      if (width && width[1]) {
        // Force a reflow by temporarily setting to a different value
        meter.style.width = '0%';
        
        // Set back to intended width
        setTimeout(() => {
          meter.style.width = width[1] + '%';
        }, 50);
      }
    }
  });
}

// Video Modal functionality
function initFlavorVideoModal(sectionId) {
  const modal = document.getElementById(`flavor-video-modal-${sectionId}`);
  if (!modal) return;
  
  const iframe = modal.querySelector('.flavor-tasting__video-iframe');
  const videoPlayer = modal.querySelector('.flavor-tasting__video-player');
  const videoSource = videoPlayer?.querySelector('source');
  const closeButton = modal.querySelector('.flavor-tasting__video-modal-close');
  const overlay = modal.querySelector('.flavor-tasting__video-modal-overlay');
  
  // Find the section container for this modal
  const sectionContainer = document.querySelector(`[data-section-id="${sectionId}"]`) || document.querySelector('.flavor-tasting');
  
  if (sectionContainer) {
    // Add click listeners to video overlays within this section
    const videoOverlays = sectionContainer.querySelectorAll('.flavor-tasting__video-overlay[data-video-url]');
    videoOverlays.forEach(videoOverlay => {
      videoOverlay.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const videoUrl = this.dataset.videoUrl;
        openVideoModal(videoUrl);
      });
    });
    
    // Add click listeners to entire media items that have videos within this section
    const mediaItemsWithVideo = sectionContainer.querySelectorAll('.flavor-tasting__media-item[data-video-url]');
    mediaItemsWithVideo.forEach(mediaItem => {
      mediaItem.style.cursor = 'pointer';
      mediaItem.addEventListener('click', function(e) {
        // Check if the click was on the video overlay (to avoid double triggering)
        if (!e.target.closest('.flavor-tasting__video-overlay')) {
          e.preventDefault();
          e.stopPropagation();
          const videoUrl = this.dataset.videoUrl;
          if (videoUrl) {
            openVideoModal(videoUrl);
          }
        }
      });
    });

    // Add click listeners to flavor titles that have videos
    const flavorTitlesWithVideo = sectionContainer.querySelectorAll('.flavor-tasting__flavor-title');
    flavorTitlesWithVideo.forEach(title => {
      const flavorItem = title.closest('.flavor-tasting__flavor-item');
      const mediaItem = flavorItem?.querySelector('.flavor-tasting__media-item[data-video-url]');
      
      if (mediaItem && mediaItem.dataset.videoUrl) {
        title.classList.add('flavor-tasting__flavor-title--clickable');
        
        title.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          const videoUrl = mediaItem.dataset.videoUrl;
          if (videoUrl) {
            openVideoModal(videoUrl);
          }
        });
      }
    });
    
  }
  
  function openVideoModal(videoUrl) {
    if (!modal || !videoUrl) return;
    
    // Check if it's a direct MP4 file
    if (isDirectVideoFile(videoUrl)) {
      // Use video element for direct MP4 files
      if (videoPlayer && videoSource) {
        videoSource.src = videoUrl;
        videoPlayer.load();
        videoPlayer.style.display = 'block';
        iframe.style.display = 'none';
        modal.style.display = 'block';
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Use iframe for YouTube, Vimeo, etc.
      const embedUrl = getEmbedUrl(videoUrl);
      if (embedUrl && iframe) {
        iframe.src = embedUrl;
        iframe.style.display = 'block';
        videoPlayer.style.display = 'none';
        modal.style.display = 'block';
        document.body.style.overflow = 'hidden';
      }
    }
  }
  
  function closeVideoModal() {
    if (!modal) return;
    
    modal.style.display = 'none';
    document.body.style.overflow = '';
    
    // Stop iframe video
    if (iframe) {
      iframe.src = '';
      iframe.style.display = 'none';
    }
    
    // Stop video player
    if (videoPlayer) {
      videoPlayer.pause();
      videoPlayer.currentTime = 0;
      videoPlayer.style.display = 'none';
      if (videoSource) {
        videoSource.src = '';
      }
    }
  }
  
  function isDirectVideoFile(url) {
    // Check if URL ends with video file extensions
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext));
  }
  
  function getEmbedUrl(url) {
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    }
    
    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)([0-9]+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    
    // If it's already an embed URL or other format, return as is
    return url;
  }
  
  // Close modal listeners
  if (closeButton) {
    closeButton.addEventListener('click', closeVideoModal);
  }
  
  if (overlay) {
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) {
        closeVideoModal();
      }
    });
  }
  
  // Close on escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modal.style.display !== 'none') {
      closeVideoModal();
    }
  });
}

// Initialize after document is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  // Add a delay to ensure all elements are properly rendered
  setTimeout(() => {
    initFlavorGalleries();
    initVideoModals();
    fixMeters();
  }, 100);
});

// Handle Shopify section loads
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section.classList.contains('flavor-tasting')) {
    setTimeout(() => {
      const sectionId = section.dataset.sectionId;
      if (sectionId) {
        initFlavorVideoModal(sectionId);
      }
      initFlavorGalleries();
      fixMeters();
    }, 100);
  }
});

// Function to initialize all flavor galleries
function initFlavorGalleries() {
  const flavorGalleries = document.querySelectorAll('.flavor-tasting__media-gallery');
  
  flavorGalleries.forEach((gallery) => {
    new FlavorGallery(gallery);
  });
}

// Function to initialize video modals
function initVideoModals() {
  const flavorModals = document.querySelectorAll('[id^="flavor-video-modal-"]');
  
  flavorModals.forEach((modal) => {
    const sectionId = modal.id.replace('flavor-video-modal-', '');
    if (sectionId) {
      initFlavorVideoModal(sectionId);
    }
  });
} 